import React from "react";
import Select, { ActionTypes } from "react-select";

export interface ISelectOption<T> {
    label: string;
    value: T;
    selected?: boolean;
    optionDisabled?: boolean
}
export interface ISelectProps<T> {
    id?: string;
    options: Array<ISelectOption<T>>;
    multi?: boolean;
    className?: string;
    isSearchable?: boolean;
    onChange: (value: T | T[] | null) => void;
    onBlur?: () => void;
    disabled?: boolean;
    placeholder?: string;
    menuPortalTarget?: HTMLElement;
}

const _Select = <T,>(props: ISelectProps<T>) => {
    const { id, options, multi, className, isSearchable, onChange, onBlur, disabled, placeholder, menuPortalTarget } = props;
    const formatValues = (value: ISelectOption<T>) => ({
        label: value.label,
        value: value.value,
        optionDisabled: value.optionDisabled
    });

    const _onChange = (value: ISelectOption<T> | Array<ISelectOption<T>>, actionType: { action: ActionTypes }) => {
        onChange(
            !value
                ? null
                : multi
                ? (value as Array<ISelectOption<T>>).map(v => v.value)
                : (value as ISelectOption<T>).value
        );
    };

    const translateOptions = () => ({
        ...(id ? { id } : null),
        ...(options ? { options: options.map(i => formatValues(i)) } : null),
        ...(options && multi
            ? {
                  value: options
                      .filter(i => i.selected)
                      .filter(i => i.value)
                      .map(i => formatValues(i))
              }
            : null),
        ...(options && !multi
            ? {
                  value: options.filter(i => i.selected).filter(i => i.value).length
                      ? options
                            .filter(i => i.selected)
                            .filter(i => i.value)
                            .map(i => formatValues(i))[0]
                      : ""
              }
            : null),
        ...(multi ? { isMulti: true } : null),
        ...(isSearchable ? { isSearchable } : null),
        ...(className ? { className } : null),
        // @ts-expect-error
        ...(onChange ? { onChange: _onChange } : null),
        ...(onBlur ? { onBlur } : null),
        ...(disabled ? { isDisabled: true } : null),
        ...(placeholder ? { placeholder } : null),
        ...(menuPortalTarget ? { menuPortalTarget } : null),
        styles: {
            menu: (provided, state) => ({ ...provided, zIndex: "1001" }),
            ...(multi
                ? {
                      valueContainer: (provided, state) => ({ ...provided, margin: "4px 0 4px 5px"}),
                      placeholder: (provided, state) => ({ ...provided, lineHeight: "14px" })
                  }
                : null)
        },
        autosize: false,
        isOptionDisabled: (option) => (option.optionDisabled || false)
    });
    return <Select {...translateOptions()} classNamePrefix="select" />;
};

export default _Select;
